/**
 * Job-seeker Position Search: parse comma-separated OR values,
 * score/rank openings, and dedupe. Fields are OR within, AND across.
 */

export function splitSearchValues(text, max = 8) {
  return String(text || "")
    .split(/[,;|]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, max);
}

export function parseYearList(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n >= 0))];
  }
  const fromText = splitSearchValues(value)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n >= 0);
  if (fromText.length) return [...new Set(fromText)];
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

function normTitle(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/full\s*-?\s*stack/g, "fullstack")
    .replace(/[^a-z0-9.+#]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normSkill(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\.net\s*core/g, "dotnetcore")
    .replace(/asp\.?\s*net\s*core/g, "dotnetcore")
    .replace(/asp\.?\s*net/g, "aspnet")
    .replace(/c\s*#/g, "csharp")
    .replace(/sql\s*server/g, "sqlserver")
    .replace(/node\.?\s*js/g, "nodejs")
    .replace(/[^a-z0-9.+#]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function titleMatches(jobTitle, wantTitle) {
  const want = normTitle(wantTitle);
  if (!want || want === "open positions") return true;
  const hay = normTitle(jobTitle);
  if (!hay) return false;
  if (hay.includes(want) || want.includes(hay)) return true;
  const tokens = want.split(" ").filter((t) => t.length >= 3);
  if (!tokens.length) return true;
  const hits = tokens.filter((t) => hay.includes(t)).length;
  return hits >= Math.max(1, Math.ceil(tokens.length * 0.5));
}

const CITY_ALIASES = {
  bangalore: ["bangalore", "bengaluru"],
  bengaluru: ["bangalore", "bengaluru"],
  mumbai: ["mumbai", "bombay"],
  bombay: ["mumbai", "bombay"],
  chennai: ["chennai", "madras"],
  madras: ["chennai", "madras"],
  gurugram: ["gurugram", "gurgaon"],
  gurgaon: ["gurugram", "gurgaon"],
  delhi: ["delhi", "newdelhi", "ncr"],
  pune: ["pune", "poona"]
};

function locationMatches(jobLoc, want) {
  const loc = String(jobLoc || "").trim().toLowerCase();
  let w = String(want || "").trim().toLowerCase();
  if (!w || w === "india" || w === "anywhere") return true;
  const remoteWanted = /remote|hybrid|wfh|work from home/.test(w);
  w = w.replace(/remote|hybrid|wfh|work from home/gi, " ").replace(/\s+/g, " ").trim();
  if (remoteWanted && /remote|hybrid|wfh|work from home/.test(loc) && !w) return true;
  if (!w) return true;
  if (!loc || loc === "india") return true;
  if (loc.includes(w) || (loc.length >= 4 && w.includes(loc))) return true;
  const key = w.replace(/[^a-z]/g, "");
  return (CITY_ALIASES[key] || [key]).some((alias) => alias && loc.includes(alias));
}

function companyKey(name) {
  let s = String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (s === "tcs" || s.includes("tataconsultancy")) s = "tcs";
  if (s.startsWith("goldpe") || s.includes("sreyas")) s = "goldpe";
  if (s.startsWith("accenture")) s = "accenture";
  if (s.startsWith("infosys")) s = "infosys";
  return s;
}

function companyMatches(jobCompany, want) {
  const wantKey = companyKey(want);
  if (!wantKey) return true;
  const jobKey = companyKey(jobCompany);
  if (!jobKey) return true;
  return jobKey.includes(wantKey) || wantKey.includes(jobKey);
}

function skillMatches(blob, skill) {
  const want = normSkill(skill);
  if (!want) return true;
  const hay = normSkill(blob);
  if (hay.includes(want)) return true;
  const compactWant = want.replace(/\s+/g, "");
  const compactHay = hay.replace(/\s+/g, "");
  return compactHay.includes(compactWant);
}

function extractYears(job) {
  const blob = `${job.title || ""} ${job.role || ""} ${job.summary || ""} ${job.skills || ""} ${job.experience || ""}`.toLowerCase();
  if (/fresher|intern|graduate|entry.?level|trainee|0\s*[-–to]+\s*1/.test(blob)) return 0;
  const range = blob.match(/(\d+)\s*[-–to]+\s*(\d+)\s*(?:years?|yrs?)/i);
  if (range) return Number(range[1]);
  const m = blob.match(/(\d+)\s*\+?\s*(?:years?|yrs?)/i);
  if (m) return Number(m[1]);
  if (/\b(senior|lead|principal|staff|architect)\b/.test(blob)) return 5;
  return null;
}

function experienceMatches(years, yearList) {
  if (!yearList.length) return true;
  if (years == null) return true;
  const min = Math.min(...yearList);
  const max = Math.max(...yearList);
  if (yearList.length === 1) return years >= min;
  return years >= min && years <= max;
}

function searchBlob(job) {
  const skills = Array.isArray(job.skills) ? job.skills.join(" ") : String(job.skills || "");
  return `${job.title || ""} ${job.role || ""} ${job.summary || ""} ${skills} ${job.location || ""} ${job.description || ""}`;
}

const WEIGHTS = {
  title: 25,
  skills: 20,
  experience: 15,
  location: 20,
  company: 20
};

export function isCompanyOnlyQuery(query = {}) {
  return (
    splitSearchValues(query.company).length > 0 &&
    splitSearchValues(query.title).length === 0 &&
    splitSearchValues(query.skills).length === 0 &&
    splitSearchValues(query.location).filter((l) => !/^(india|anywhere)$/i.test(l)).length === 0 &&
    parseYearList(query.experienceYears ?? query.minYears).length === 0
  );
}

export function scorePosition(job, query = {}) {
  const titles = splitSearchValues(query.title);
  const skills = splitSearchValues(query.skills);
  const locations = splitSearchValues(query.location);
  const companies = splitSearchValues(query.company);
  const yearList = parseYearList(query.experienceYears ?? query.minYears);
  const blob = searchBlob(job);
  const years = extractYears(job);

  const provided = {
    title: titles.length > 0,
    skills: skills.length > 0,
    experience: yearList.length > 0,
    location: locations.length > 0,
    company: companies.length > 0
  };

  const matchedCriteria = {
    title: !provided.title || titles.some((t) => titleMatches(job.title || job.role || "", t)),
    skills: !provided.skills || skills.some((s) => skillMatches(blob, s)),
    experience: !provided.experience || experienceMatches(years, yearList),
    location: !provided.location || locations.some((l) => locationMatches(job.location, l)),
    company: !provided.company || companies.some((c) => companyMatches(job.company, c))
  };

  let total = 0;
  let earned = 0;
  Object.keys(WEIGHTS).forEach((key) => {
    if (!provided[key]) return;
    total += WEIGHTS[key];
    if (matchedCriteria[key]) earned += WEIGHTS[key];
  });
  const matchPercent = total ? Math.round((earned / total) * 100) : 0;
  const andMatch = Object.keys(WEIGHTS).every((key) => !provided[key] || matchedCriteria[key]);

  return {
    ...job,
    years,
    matchPercent,
    matchedCriteria,
    andMatch,
    providedCriteria: provided
  };
}

function dedupeKey(job) {
  const title = String(job.title || job.role || "").toLowerCase().replace(/\s+/g, " ").trim();
  const company = companyKey(job.company);
  const raw = String(job.url || job.applyUrl || "").trim().toLowerCase().split("#")[0];
  if (/[?&](posting|job|jobid|gh_jid|lid)=/.test(raw)) {
    return `u:${raw}`;
  }
  const path = raw.split("?")[0].replace(/\/+$/, "");
  if (path && /\/jobs?\/.+|\/posting\/|lever\.co\/|greenhouse\.io\/|ashbyhq\.com\//.test(path) && path.length > 20) {
    return `u:${path}`;
  }
  return `${company}|${title}`;
}

export function rankPositions(jobs, query = {}) {
  const seen = {};
  const scored = [];
  (jobs || []).forEach((job, idx) => {
    if (!job || !String(job.title || job.role || "").trim()) return;
    const ranked = scorePosition(job, query);
    const key = dedupeKey(ranked);
    const prev = seen[key];
    if (prev != null) {
      if ((ranked.matchPercent || 0) > (scored[prev].matchPercent || 0)) scored[prev] = ranked;
      return;
    }
    seen[key] = scored.length;
    scored.push({ ...ranked, id: ranked.id || `pos_${idx}` });
  });

  const andHits = scored.filter((j) => j.andMatch);
  const hasCompany = splitSearchValues(query.company).length > 0;
  const closest = scored.filter((j) => j.matchPercent >= 40);
  const listBase = hasCompany
    ? scored
    : (andHits.length ? andHits : (closest.length ? closest : scored));
  const usedFallback = !hasCompany && andHits.length === 0 && listBase.length > 0;
  const list = listBase
    .slice()
    .sort((a, b) => (b.matchPercent - a.matchPercent) || String(a.title).localeCompare(String(b.title)));

  return {
    positions: list,
    totalScored: scored.length,
    andMatchCount: andHits.length,
    usedFallback
  };
}

export function yearsLabel(job) {
  if (job.years == null && job.years !== 0) return "";
  return `${job.years} Year${job.years === 1 ? "" : "s"}`;
}
